"use strict";
import { SharedEventName } from "@uniswap/analytics-events";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { CheckCircleFilled } from "ui/src/components/icons/CheckCircleFilled";
import { CoinConvert } from "ui/src/components/icons/CoinConvert";
import { CopyAlt } from "ui/src/components/icons/CopyAlt";
import { Heart } from "ui/src/components/icons/Heart";
import { InfoCircleFilled } from "ui/src/components/icons/InfoCircleFilled";
import { ReceiveAlt } from "ui/src/components/icons/ReceiveAlt";
import { ShareArrow } from "ui/src/components/icons/ShareArrow";
import { ContextMenu } from "components/menus/ContextMenuV2";
import { ContextMenuTriggerMode } from "components/menus/types";
import { UNISWAP_WEB_URL } from "constants/urls";
import { useAccountMeta, useUniswapContext } from "contexts/UniswapContext";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { useSelectHasTokenFavorited } from "features/favorites/useSelectHasTokenFavorited";
import { useToggleFavoriteCallback } from "features/favorites/useToggleFavoriteCallback";
import { pushNotification } from "features/notifications/slice";
import { AppNotificationType, CopyNotificationType } from "features/notifications/types";
import { ElementName } from "features/telemetry/constants";
import { sendAnalyticsEvent } from "features/telemetry/send.web";
import { setClipboard } from "utils/clipboard";
import { currencyAddress, currencyId, currencyIdToAddress, currencyIdToChain } from "utils/uniswapCurrencyId";
import { getTokenDetailsURL } from "utils/linking";
import { isWeb } from "utilities/src/platform";
import { useTrace } from "utilities/src/telemetry/trace/TraceContext";
const COPY_CLOSE_DELAY = 400;
export var TokenContextMenuAction = /* @__PURE__ */ ((TokenContextMenuAction2) => {
  TokenContextMenuAction2["CopyAddress"] = "copyAddress";
  TokenContextMenuAction2["Favorite"] = "favorite";
  TokenContextMenuAction2["Swap"] = "swap";
  TokenContextMenuAction2["Send"] = "send";
  TokenContextMenuAction2["Receive"] = "receive";
  TokenContextMenuAction2["Share"] = "share";
  TokenContextMenuAction2["ViewDetails"] = "viewDetails";
  TokenContextMenuAction2["HideToken"] = "hideToken";
  return TokenContextMenuAction2;
})(TokenContextMenuAction || {});
function _TokenOptionItemContextMenu({
  children,
  currency,
  isOpen,
  openMenu,
  closeMenu,
  triggerMode = ContextMenuTriggerMode.Secondary,
  actions
}) {
  const { t } = useTranslation();
  const account = useAccountMeta();
  const { navigateToTokenDetails, navigateToSwapFlow, navigateToSendFlow, navigateToReceive, handleShareToken } = useUniswapContext();
  const dispatch = useDispatch();
  const { isTestnetModeEnabled } = useEnabledChains();
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const trace = useTrace();
  const id = currencyId(currency);
  const onNavigateToTokenDetails = useCallback(() => {
    if (isTestnetModeEnabled) {
      return;
    }
    closeMenu();
    navigateToTokenDetails(id);
    sendAnalyticsEvent(SharedEventName.ELEMENT_CLICKED, {
      element: ElementName.TokenItem,
      ...trace
    });
  }, [isTestnetModeEnabled, closeMenu, navigateToTokenDetails, id, trace]);
  const onCopyAddress = useCallback(async () => {
    await setClipboard(currencyAddress(currency));
    if (!isWeb) {
      dispatch(
        pushNotification({
          type: AppNotificationType.Copied,
          copyType: CopyNotificationType.Address
        })
      );
    }
    setCopiedAddress(true);
    setTimeout(() => {
      setCopiedAddress(false);
      closeMenu();
    }, COPY_CLOSE_DELAY);
  }, [dispatch, currency, closeMenu]);
  const isFavoriteToken = useSelectHasTokenFavorited(id);
  const toggleFavorite = useToggleFavoriteCallback(id, isFavoriteToken);
  const onToggleFavorite = useCallback(() => {
    toggleFavorite();
    closeMenu();
  }, [closeMenu, toggleFavorite]);
  const onNavigateToSwap = useCallback(() => {
    closeMenu();
    navigateToSwapFlow({ outputCurrencyId: id });
    sendAnalyticsEvent(SharedEventName.ELEMENT_CLICKED, {
      element: ElementName.Swap,
      ...trace
    });
  }, [closeMenu, navigateToSwapFlow, id, trace]);
  const onNavigateToSend = useCallback(() => {
    closeMenu();
    navigateToSendFlow({ chainId: currency.chainId, currencyAddress: currencyAddress(currency) });
  }, [closeMenu, currency, navigateToSendFlow]);
  const onNavigateToReceive = useCallback(() => {
    closeMenu();
    navigateToReceive();
  }, [closeMenu, navigateToReceive]);
  const onShare = useCallback(async () => {
    if (isWeb) {
      const url = UNISWAP_WEB_URL + getTokenDetailsURL({
        address: currencyIdToAddress(id),
        chain: currencyIdToChain(id) ?? void 0
      });
      await setClipboard(url);
      setCopiedUrl(true);
      setTimeout(() => {
        setCopiedUrl(false);
        closeMenu();
      }, COPY_CLOSE_DELAY);
    } else {
      handleShareToken({ currencyId: id });
      closeMenu();
    }
  }, [closeMenu, handleShareToken, id]);
  const dropdownOptions = useMemo(() => {
    const options = [];
    if (actions.includes("copyAddress" /* CopyAddress */)) {
      if (isWeb) {
        options.push({
          onPress: onCopyAddress,
          disabled: currency.isNative,
          label: copiedAddress ? t("notification.copied.address") : t("common.copy.address"),
          Icon: copiedAddress ? CheckCircleFilled : CopyAlt,
          closeDelay: COPY_CLOSE_DELAY,
          iconColor: copiedAddress ? "$statusSuccess" : "$neutral2"
        });
      } else {
        options.push({
          onPress: onCopyAddress,
          disabled: currency.isNative,
          label: t("common.copy.address"),
          Icon: CopyAlt,
          iconColor: "$neutral2"
        });
      }
    }
    if (actions.includes("favorite" /* Favorite */)) {
      options.push({
        onPress: onToggleFavorite,
        label: isFavoriteToken ? t("explore.wallets.favorite.action.remove.short") : t("explore.tokens.favorite.action.add"),
        Icon: Heart,
        closeDelay: COPY_CLOSE_DELAY,
        iconColor: isFavoriteToken ? "$accent1" : "$neutral2"
      });
    }
    if (actions.includes("swap" /* Swap */)) {
      options.push({
        onPress: onNavigateToSwap,
        label: t("common.button.swap"),
        Icon: CoinConvert,
        iconColor: "$neutral2"
      });
    }
    if (account && actions.includes("receive" /* Receive */)) {
      options.push({
        onPress: onNavigateToReceive,
        label: t("common.button.receive"),
        Icon: ReceiveAlt,
        iconColor: "$neutral2"
      });
    }
    if (actions.includes("share" /* Share */)) {
      options.push({
        onPress: onShare,
        label: copiedUrl ? t("notification.copied.linkUrl") : t("common.button.share"),
        Icon: copiedUrl ? CheckCircleFilled : ShareArrow,
        closeDelay: COPY_CLOSE_DELAY,
        iconColor: copiedUrl ? "$statusSuccess" : "$neutral2"
      });
    }
    if (actions.includes("viewDetails" /* ViewDetails */)) {
      options.push({
        onPress: onNavigateToTokenDetails,
        label: t("token.details"),
        Icon: InfoCircleFilled,
        iconColor: "$neutral2"
      });
    }
    return options;
  }, [
    actions,
    account,
    onCopyAddress,
    currency.isNative,
    copiedAddress,
    t,
    onToggleFavorite,
    isFavoriteToken,
    onNavigateToSwap,
    onNavigateToSend,
    onNavigateToReceive,
    onShare,
    copiedUrl,
    onNavigateToTokenDetails
  ]);
  return <ContextMenu
    menuItems={dropdownOptions}
    triggerMode={triggerMode}
    isOpen={isOpen}
    closeMenu={closeMenu}
    openMenu={openMenu}
    offsetY={4}
  >
      {children}
    </ContextMenu>;
}
export const TokenOptionItemContextMenu = React.memo(_TokenOptionItemContextMenu);
